import { ShieldCheck } from 'lucide-react';

import type { MeCertificadoDigital, MeResponse } from '@/api/generated/model';

/** O que o rodapé do documento vai mostrar, antes de salvar. */
export interface AssinaturaEmPrevia {
  tipo: string;
  texto: string | null;
  fonte: string | null;
  url: string | null;
  validoAte: string | null;
  /** Presente = o PDF sai assinado digitalmente, e o rodapé diz isso. */
  certificado?: MeCertificadoDigital | null;
}

/**
 * Réplica do rodapé do receituário e da requisição.
 *
 * ⚠️ A prévia usa as MESMAS fontes `.ttf` que o `@react-pdf` embute no
 * documento (`public/fontes/`, declaradas em `index.css`). É o que garante que
 * o que a pessoa aprova aqui é o que sai impresso — na V1 a prévia vinha do
 * Google Fonts e o PDF era rasterizado no `<canvas>`, então nem sempre batiam.
 *
 * ⚠️ **O bloco é PAPEL, não superfície do app: `bg-papel` e `text-papel-*` não
 * têm variante escura de propósito.** Antes ele seguia o tema
 * (`bg-muted/30` + `text-foreground`), e no tema escuro a rubrica desaparecia —
 * o PNG da `AssinaturaDesenhada` tem traço `#1a1a2e` sobre fundo transparente,
 * e traço quase preto em fundo quase preto lê-se como "a assinatura não
 * carregou". A imagem estava lá; o contraste, não. Inverter a imagem no escuro
 * (`dark:invert`) resolveria a rubrica e estragaria a assinatura digitalizada,
 * que costuma vir com fundo branco opaco. O documento sai em papel branco nos
 * dois temas, então a prévia também.
 *
 * Sem data/hora, ao contrário da V1: o documento carrega a data da EMISSÃO, e
 * mostrar "agora" aqui sugeria que a assinatura carimba o momento da
 * configuração.
 */
export function PreviaDaAssinatura({
  assinatura,
  me,
}: {
  assinatura: AssinaturaEmPrevia;
  me: MeResponse;
}) {
  const registro = me.perfil.crmv
    ? `CRMV ${me.perfil.crmv}${me.perfil.ufCrmv ? `-${me.perfil.ufCrmv}` : ''}`
    : null;

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground text-center font-medium uppercase tracking-wide">
        Como vai sair no documento
      </p>

      <div className="border border-border rounded-xl p-6 bg-papel">
        <div className="space-y-2">
          <div className="min-h-[56px] flex items-end justify-center pb-1">
            <Desenho assinatura={assinatura} nomeDoPerfil={me.perfil.nomeCompleto} />
          </div>

          <div className="w-56 mx-auto border-t border-papel-texto" />

          <div className="text-center space-y-0.5">
            <p className="text-sm font-semibold text-papel-texto">{me.perfil.nomeCompleto}</p>
            {registro && <p className="text-xs text-papel-suave">{registro}</p>}
            {assinatura.tipo === 'CERTIFICADO' && (
              <p className="text-[10px] text-papel-suave flex items-center justify-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                {textoDoCertificado(assinatura, me.perfil.nomeCompleto)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Desenho({
  assinatura,
  nomeDoPerfil,
}: {
  assinatura: AssinaturaEmPrevia;
  nomeDoPerfil: string;
}) {
  if (assinatura.tipo === 'DIGITADA' && assinatura.texto) {
    return (
      <span
        // A família vem da chave gravada em `Profile.assinaturaFonte`, que é o
        // mesmo nome do `@font-face` e da fonte registrada no PDF.
        style={{ fontFamily: `'${assinatura.fonte ?? 'Great Vibes'}', cursive` }}
        className="text-4xl text-papel-texto leading-none"
      >
        {assinatura.texto}
      </span>
    );
  }

  if ((assinatura.tipo === 'IMAGEM' || assinatura.tipo === 'DESENHADA') && assinatura.url) {
    return (
      <img src={assinatura.url} alt="Assinatura" className="max-h-14 object-contain" />
    );
  }

  // NENHUMA e CERTIFICADO: espaço em branco acima da linha. No primeiro para
  // assinar à caneta; no segundo porque a identificação vai abaixo da linha.
  return <span className="sr-only">Sem desenho de assinatura — {nomeDoPerfil}</span>;
}

/**
 * O MESMO texto do PDF (`textoDoCertificado` em `infra/pdf/documentos/comuns.tsx`
 * no backend), e pela mesma regra: só diz "assinado digitalmente" quando existe
 * certificado que assina de verdade — A1, SafeID ou SerproID. O `.cer` legado só
 * identifica o titular, e o texto fica modesto.
 */
function textoDoCertificado(assinatura: AssinaturaEmPrevia, nomeDoPerfil: string): string {
  const titular = assinatura.texto ?? nomeDoPerfil;
  const validade = assinatura.validoAte
    ? new Date(assinatura.validoAte).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    : null;

  if (assinatura.certificado) {
    return [
      `Assinado digitalmente por ${titular}`,
      validade ? `certificado válido até ${validade}` : null,
      'confira em validar.iti.gov.br',
    ]
      .filter(Boolean)
      .join(' — ');
  }

  const identificacao = `Certificado digital de ${titular}`;
  return validade ? `${identificacao} — válido até ${validade}` : identificacao;
}
